/**
 * Tigmi Add-On Block
 * Handles swatch selection, image swap, price update and add to cart for the
 * `tigmi_addon` block rendered by snippets/tigmi-addon.liquid.
 */

(function() {
  'use strict';

  function initTigmiAddon() {
    const blocks = document.querySelectorAll('[data-tigmi-addon]');

    blocks.forEach(function(block) {
      // shopify:section:load re-scans the whole document, so a block that was
      // not reloaded would otherwise get a second set of listeners and fire
      // every handler twice.
      if (block.tigmiAddonInstance) return;
      block.tigmiAddonInstance = new TigmiAddonBlock(block);
    });
  }

  function TigmiAddonBlock(container) {
    this.container = container;
    this.productId = container.dataset.tigmiAddonProductId;

    this.quantityInput = container.querySelector('[data-tigmi-addon-qty-input]');
    this.addToCartBtn = container.querySelector('[data-tigmi-addon-add-btn]');
    // The tiles are the imagery now: there is no hero. A variant's picture is
    // swapped into the selected tile, and the tile's frame is a fixed ratio, so
    // no image swap can change the block's height.
    this.tiles = Array.prototype.slice.call(
      container.querySelectorAll('[data-tigmi-addon-product-swatch]')
    );
    this.detailsLink = container.querySelector('[data-tigmi-addon-details-link]');
    this.priceEl = container.querySelector('[data-tigmi-addon-price]');
    this.comparePriceEl = container.querySelector('[data-tigmi-addon-compare-price]');
    this.priceWrapperEl = container.querySelector('[data-tigmi-addon-price-wrapper]');
    // Set by the Liquid only when the heading matches the promo trigger.
    this.promoProductId = container.dataset.tigmiAddonPromoProductId || null;
    this.promoLabel = container.dataset.tigmiAddonPromoLabel || 'FREE';
    this.selectedValueEl = container.querySelector('[data-tigmi-addon-selected-value]');
    this.addTextEl = container.querySelector('[data-tigmi-addon-add-text]');
    this.spinnerEl = container.querySelector('[data-tigmi-addon-spinner]');

    // Row 1 (products) and row 2 (variants of the selected product)
    // One per add-on, stacked in a single grid cell by the CSS. Toggled with
    // `hidden`, which that CSS turns into visibility rather than display, so
    // the reserved height never changes.
    this.summaryEls = Array.prototype.slice.call(
      container.querySelectorAll('[data-tigmi-addon-summary]')
    );
    this.variantRow = container.querySelector('[data-tigmi-addon-variant-row]');
    this.variantGroups = Array.prototype.slice.call(
      container.querySelectorAll('[data-tigmi-addon-variant-group]')
    );

    this.statusEl = container.querySelector('[data-tigmi-addon-status]');

    this.selectedProductId = container.dataset.tigmiAddonProductId;

    // The add button's idle state is re-derived from the live selection rather
    // than from a snapshot taken before the request, so switching swatch while
    // the "Added!" state is showing cannot leave a stale label or re-enable the
    // button for a sold-out variant.
    this.selectedAvailable = !this.addToCartBtn.disabled;
    this.selectedMax = this.quantityInput && this.quantityInput.max
      ? parseInt(this.quantityInput.max, 10)
      : null;
    this.resetTimer = null;
    this.isAdding = false;

    this.bindEvents();
    this.updateVariantRow();
    this.syncToSelectedSwatch();
  }

  /**
   * Re-applies whatever swatch is marked selected, so the first paint always
   * agrees with the swatch data. The swatch attributes are rendered inside a
   * loop over the metafield and are reliable; the block's opening state is
   * derived separately and has been the weaker of the two.
   */
  TigmiAddonBlock.prototype.syncToSelectedSwatch = function() {
    var active = this.variantGroups.filter(function(g) { return !g.hidden; })[0];

    var selected = (active && active.querySelector('.tigmi-addon__swatch--selected'))
      || this.container.querySelector('[data-tigmi-addon-product-swatch].tigmi-addon__swatch--selected');

    if (selected) this.applyVariant(selected.dataset);
  };

  TigmiAddonBlock.prototype.bindEvents = function() {
    var self = this;

    // Quantity buttons
    var qtyBtns = this.container.querySelectorAll('[data-tigmi-addon-qty-action]');
    qtyBtns.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        self.handleQuantityChange(e);
      });
    });

    // Quantity input
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', function() {
        self.validateQuantity();
      });
    }

    // Row 1: product swatches
    var productSwatches = this.container.querySelectorAll('[data-tigmi-addon-product-swatch]');
    productSwatches.forEach(function(swatch) {
      swatch.addEventListener('click', function(e) {
        self.handleProductSwatchClick(e);
      });
    });

    // Row 2: variant swatches
    var swatches = this.container.querySelectorAll('[data-tigmi-addon-swatch]');
    swatches.forEach(function(swatch) {
      swatch.addEventListener('click', function(e) {
        self.handleSwatchClick(e);
      });
    });

    // Add to cart
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', function(e) {
        self.handleAddToCart(e);
      });
    }
  };

  TigmiAddonBlock.prototype.handleQuantityChange = function(event) {
    var action = event.currentTarget.dataset.tigmiAddonQtyAction;
    var value = parseInt(this.quantityInput.value, 10) || 1;

    this.quantityInput.value = this.clampQuantity(
      action === 'increase' ? value + 1 : value - 1
    );
  };

  TigmiAddonBlock.prototype.validateQuantity = function() {
    this.quantityInput.value = this.clampQuantity(parseInt(this.quantityInput.value, 10));
  };

  /**
   * Floors at 1 and, when Shopify tracks stock for the selected variant and it
   * is not set to keep selling, caps at what is actually on hand. Stops the
   * stepper from walking straight into a 422 from cart/add.js.
   */
  TigmiAddonBlock.prototype.clampQuantity = function(value) {
    if (isNaN(value) || value < 1) value = 1;

    var max = this.selectedMax;
    if (max !== null && !isNaN(max) && max >= 1 && value > max) {
      value = max;
      this.announce('Only ' + max + ' available');
    }

    return value;
  };

  TigmiAddonBlock.prototype.announce = function(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  };

  TigmiAddonBlock.prototype.addToCartLabel = function() {
    var t = window.theme;
    if (this.selectedAvailable) {
      return t && t.strings && t.strings.addToCart ? t.strings.addToCart : 'Add to Cart';
    }
    return t && t.strings && t.strings.soldOut ? t.strings.soldOut : 'Sold Out';
  };

  /**
   * Returns the button to its idle state for whatever is selected right now.
   */
  TigmiAddonBlock.prototype.resetAddButton = function() {
    var btn = this.addToCartBtn;
    btn.classList.remove('is-loading', 'is-success');
    btn.disabled = !this.selectedAvailable;
    if (this.addTextEl) this.addTextEl.textContent = this.addToCartLabel();
  };

  /**
   * Row 1. Selecting a product reveals that product's variant group, moves the
   * selection to its default variant and re-decides whether row 2 is shown.
   */
  TigmiAddonBlock.prototype.handleProductSwatchClick = function(event) {
    var swatch = event.currentTarget;

    if (swatch.classList.contains('tigmi-addon__tile--disabled')) return;

    this.setPressed(
      this.container.querySelectorAll('[data-tigmi-addon-product-swatch]'),
      swatch,
      'tigmi-addon__tile--selected'
    );

    this.selectedProductId = swatch.dataset.productId;
    this.container.dataset.tigmiAddonProductId = this.selectedProductId;

    this.updateSummary();
    this.updateDetailsLink(swatch.dataset.productUrl);

    var group = this.updateVariantRow();

    // If the newly selected product has its own swatches, adopt that group's
    // selected variant rather than the product's default, so the highlighted
    // swatch and the price always agree.
    var groupSelected = group
      ? group.querySelector('.tigmi-addon__swatch--selected') || group.querySelector('[data-tigmi-addon-swatch]:not(.tigmi-addon__swatch--disabled)')
      : null;

    if (groupSelected) {
      this.setPressed(group.querySelectorAll('[data-tigmi-addon-swatch]'), groupSelected);
      this.applyVariant(groupSelected.dataset);
    } else {
      this.applyVariant(swatch.dataset);
    }
  };

  /**
   * Row 2. Selecting a variant only ever moves the selection within the
   * currently visible group.
   */
  TigmiAddonBlock.prototype.handleSwatchClick = function(event) {
    var swatch = event.currentTarget;

    if (swatch.classList.contains('tigmi-addon__swatch--disabled')) return;

    var group = swatch.closest('[data-tigmi-addon-variant-group]');
    var siblings = group
      ? group.querySelectorAll('[data-tigmi-addon-swatch]')
      : this.container.querySelectorAll('[data-tigmi-addon-swatch]');

    this.setPressed(siblings, swatch);
    this.applyVariant(swatch.dataset);
  };

  /**
   * The <img> inside whichever tile is currently selected. Everything that used
   * to write to the hero writes here instead.
   */
  TigmiAddonBlock.prototype.selectedTileImage = function() {
    var self = this;
    var tile = null;

    this.tiles.forEach(function(el) {
      if (el.dataset.productId === self.selectedProductId) tile = el;
    });

    return tile ? tile.querySelector('[data-tigmi-addon-tile-image]') : null;
  };

  /**
   * Points "View product details" at the selected add-on's own page.
   */
  TigmiAddonBlock.prototype.updateDetailsLink = function(url) {
    if (this.detailsLink && url) this.detailsLink.href = url;
  };

  /**
   * Shows the selected add-on's summary and hides the rest. They share one grid
   * cell, so this never changes the block's height.
   */
  TigmiAddonBlock.prototype.updateSummary = function() {
    var self = this;

    this.summaryEls.forEach(function(el) {
      el.hidden = el.dataset.productId !== self.selectedProductId;
    });
  };

  /**
   * Shows the variant group belonging to the selected product and hides row 2
   * entirely when that group holds fewer than two swatches. Returns the group.
   */
  TigmiAddonBlock.prototype.updateVariantRow = function() {
    var self = this;
    var active = null;

    this.variantGroups.forEach(function(group) {
      var isActive = group.dataset.productId === self.selectedProductId;
      group.hidden = !isActive;
      if (isActive) active = group;
    });

    if (this.variantRow) {
      var count = active ? active.querySelectorAll('[data-tigmi-addon-swatch]').length : 0;
      this.variantRow.hidden = count < 2;
    }

    return active;
  };

  TigmiAddonBlock.prototype.setPressed = function(group, selected, modifier) {
    var cls = modifier || 'tigmi-addon__swatch--selected';

    group.forEach(function(el) {
      var isSelected = el === selected;
      el.classList.toggle(cls, isSelected);
      el.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  };

  /**
   * Single write path for the price, image and add button, shared by both rows.
   * `data` is a swatch's dataset: every price here comes from variant data.
   */
  TigmiAddonBlock.prototype.applyVariant = function(data) {
    var self = this;

    var variantId = data.variantId;
    var variantPrice = parseInt(data.variantPrice, 10);
    var variantComparePrice = parseInt(data.variantComparePrice, 10);
    var variantAvailable = data.variantAvailable === 'true';
    var variantImage = data.variantImage;
    var selectedValue = data.value;

    // Selection state, read back by clampQuantity and resetAddButton
    this.selectedAvailable = variantAvailable;
    this.selectedMax = data.variantMax === '' || data.variantMax === undefined
      ? null
      : parseInt(data.variantMax, 10);

    // A pending success/error restore must not resurrect the previous variant's
    // label once the shopper has moved on.
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }

    this.addToCartBtn.dataset.variantId = variantId;
    this.resetAddButton();

    if (this.quantityInput) {
      if (this.selectedMax !== null && !isNaN(this.selectedMax) && this.selectedMax >= 1) {
        this.quantityInput.max = this.selectedMax;
      } else {
        this.quantityInput.removeAttribute('max');
      }
      this.quantityInput.value = this.clampQuantity(parseInt(this.quantityInput.value, 10));
    }

    // Price. innerHTML, not textContent: shop.money_format wraps the amount in
    // markup (e.g. "<span class=money>${{amount}}</span>"), same as the Liquid
    // `| money` filter that rendered the initial price.
    //
    // Promo add-on (display only, see the snippet): the regular price is struck
    // through and the price itself reads the promo label. Every swatch carries
    // its product id, so this holds for all of the promo add-on's variants.
    var isPromo = !!this.promoProductId && data.productId === this.promoProductId;

    if (this.priceWrapperEl) {
      this.priceWrapperEl.classList.toggle('tigmi-addon__price-wrapper--promo', isPromo);
    }

    if (isPromo && !isNaN(variantPrice)) {
      // Inner span, so the label can be set smaller while the outer price
      // span keeps the normal line height (see custom-pdp.css).
      if (this.priceEl) this.priceEl.innerHTML = '<span class="tigmi-addon__promo-label">' + escapeHtml(this.promoLabel) + '</span>';
      if (this.comparePriceEl) {
        this.comparePriceEl.innerHTML = this.formatMoney(variantPrice);
        this.comparePriceEl.hidden = false;
      }
    } else {
      if (this.priceEl && !isNaN(variantPrice)) {
        this.priceEl.innerHTML = this.formatMoney(variantPrice);
      }

      if (this.comparePriceEl) {
        var onSale = !isNaN(variantComparePrice) && variantComparePrice > variantPrice;
        this.comparePriceEl.innerHTML = onSale ? this.formatMoney(variantComparePrice) : '';
        this.comparePriceEl.hidden = !onSale;
      }
    }

    if (this.selectedValueEl && selectedValue) {
      this.selectedValueEl.textContent = selectedValue;
    }

    // Image. A variant's picture goes into the tile the shopper is looking at.
    // The tile frame is a fixed ratio, so unlike the old hero there is nothing
    // to resize here and the swap cannot move anything below it.
    var tileImage = this.selectedTileImage();

    if (variantImage && tileImage && variantImage !== tileImage.src) {
      tileImage.classList.add('is-loading');
      var newImg = new Image();
      newImg.onload = function() {
        tileImage.src = variantImage;
        tileImage.classList.remove('is-loading');
      };
      newImg.onerror = function() {
        tileImage.classList.remove('is-loading');
      };
      newImg.src = variantImage;
    }
  };

  TigmiAddonBlock.prototype.handleAddToCart = function(event) {
    event.preventDefault();

    var self = this;
    var btn = this.addToCartBtn;
    var variantId = btn.dataset.variantId;

    // isAdding, not btn.disabled: the button is also disabled for sold-out
    // variants, and a second click must not slip through between the fetch
    // starting and the disabled flag landing.
    if (!variantId || btn.disabled || this.isAdding) return;

    var quantity = this.quantityInput
      ? this.clampQuantity(parseInt(this.quantityInput.value, 10))
      : 1;
    if (this.quantityInput) this.quantityInput.value = quantity;

    this.isAdding = true;
    btn.classList.add('is-loading');
    btn.disabled = true;

    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: quantity }]
      })
    })
    .then(function(response) {
      return response.json().then(function(data) {
        if (!response.ok) throw data;
        return data;
      });
    })
    .then(function(data) {
      self.isAdding = false;
      btn.classList.remove('is-loading');
      btn.classList.add('is-success');

      if (self.addTextEl) {
        self.addTextEl.textContent = window.theme && window.theme.strings && window.theme.strings.added
          ? window.theme.strings.added
          : 'Added!';
      }
      self.announce('Added to cart');

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));

      // Same "Added to cart" modal the theme's own Add to Cart shows
      var lineItem = (data && data.items && data.items[0]) ? data.items[0] : data;
      self.showAddedToCartModal(lineItem);
      self.refreshCartInHeader();

      self.resetTimer = setTimeout(function() {
        self.resetTimer = null;
        self.resetAddButton();
      }, 2000);
    })
    .catch(function(error) {
      console.error('Tigmi add-on add to cart error:', error);

      self.isAdding = false;
      btn.classList.remove('is-loading');

      // Shopify puts the useful text in `description` on a 422 (sold out, or
      // more requested than is in stock).
      var message = (error && (error.description || error.message)) || 'Error';

      if (self.addTextEl) self.addTextEl.textContent = message;
      self.announce(message);

      self.resetTimer = setTimeout(function() {
        self.resetTimer = null;
        self.resetAddButton();
      }, 3000);
    });
  };

  /**
   * Builds the exact same "#added-to-cart" modal that theme.applyAjaxToProductForm
   * renders for a normal Add to Cart (see theme.js, showThemeModal call).
   */
  TigmiAddonBlock.prototype.showAddedToCartModal = function(item) {
    var t = window.theme;

    if (typeof window.showThemeModal !== 'function' || !t || !t.strings || !t.routes) {
      return;
    }

    var thumbUrl = '';
    if (t.Shopify && typeof t.Shopify.formatImage === 'function' && item.image) {
      thumbUrl = t.Shopify.formatImage(item.image, '300x');
    } else if (item.image) {
      thumbUrl = item.image;
    }

    // Preload the thumbnail so the modal doesn't pop in empty
    if (thumbUrl) {
      var img = new Image();
      img.src = thumbUrl;
    }

    var imgAlt = (item.featured_image && item.featured_image.alt) ? item.featured_image.alt : '';

    var metaHtml = '';
    if (item.price) {
      metaHtml += '<p class="cart-product__content__price">' + this.formatMoney(item.price) + '</p>';
    }
    if (item.options_with_values && item.options_with_values.length) {
      item.options_with_values.forEach(function(option) {
        if (option.name !== 'Title' && option.value !== 'Default Title') {
          metaHtml += '<p class="cart-product__content__meta">' + escapeHtml(option.name) + ': ' + escapeHtml(option.value) + '</p>';
        }
      });
    }

    // Match the theme: offset the modal below the nav bar when it isn't inline
    var offset = 25;
    try {
      var nav = t.Nav();
      if (nav && nav.bar && nav.bar.getPositionSetting() !== 'inline') {
        offset = nav.bar.height();
      }
    } catch (e) { /* keep the default offset */ }

    var tick = t.icons && t.icons.tick ? t.icons.tick : '';

    showThemeModal([
      '<div id="added-to-cart" class="theme-modal theme-modal--small" role="dialog" aria-modal="true" aria-labelledby="added-to-cart-title">',
      '<div class="inner" style="top:' + offset + 'px">',
      '<a href="#" data-modal-close class="modal-close">&times;</a>',
      '<h4 id="added-to-cart-title">' + tick + t.strings.productAddedToCart + '</h4>',
      '<div class="cart-product">',
      '<div class="cart-product__image"><img src="' + thumbUrl + '" alt="' + escapeHtml(imgAlt) + '"/></div>',
      '<div class="cart-product__content">',
      '<p class="cart-product__content__title">' + escapeHtml(item.product_title || item.title || '') + '</p>',
      metaHtml,
      '</div>',
      '</div>',
      '<p class="links">',
      '<a href="' + t.routes.cart_url + '" class="button alt">' + t.strings.viewCart + '</a>',
      '<a href="' + t.routes.checkout + '" class="button button--checkout">' + t.strings.popupCheckout + '</a> ',
      '</p>',
      '</div>',
      '</div>'
    ].join(''), 'added-to-cart', null);
  };

  /**
   * Same header/cart refresh the theme does after an ajax add.
   */
  TigmiAddonBlock.prototype.refreshCartInHeader = function() {
    var t = window.theme;
    var $ = window.jQuery;

    if (!$ || !t || !t.routes) return;

    $.get(t.routes.cart_url, function(data) {
      var cartUpdateSelector = '#site-control .cart:not(.nav-search), [data-section-type="cart-template"]';
      var $newCartObj = $($.parseHTML('<div>' + data + '</div>')).find(cartUpdateSelector);
      $(cartUpdateSelector).each(function(index) {
        $($newCartObj[index]).find('[data-cc-animate]').removeAttr('data-cc-animate');
        $(this).replaceWith($newCartObj[index]);
        $(this).parent().find('[data-cc-animate]').removeAttr('data-cc-animate');
      });
    });
  };

  function escapeHtml(str) {
    var span = document.createElement('span');
    span.innerText = str == null ? '' : str;
    return span.innerHTML;
  }

  TigmiAddonBlock.prototype.formatMoney = function(cents) {
    var t = window.theme;

    // Prefer the theme's own formatter: it handles thousands separators.
    // money_format (not the _with_code_preference variant) to stay consistent
    // with the Liquid `| money` filter that rendered this block's initial price.
    if (t && t.Shopify && typeof t.Shopify.formatMoney === 'function') {
      return t.Shopify.formatMoney(cents, t.money_format);
    }

    var format = t && t.money_format ? t.money_format : '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTigmiAddon);
  } else {
    initTigmiAddon();
  }

  // Re-initialize on Shopify section reload (Theme Editor)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('[data-tigmi-addon]')) {
      initTigmiAddon();
    }
  });

})();
